import { c as _c } from "react/compiler-runtime";
import { DangerButton, FieldLabel, FormInput, FormInputPhone, Modal, ModalSizeEnum, NeutralButton, PrimaryButton, RadioGroup } from '@systemeio/ui';
import { eventLocationLocoKeys } from 'modules/calendar/constants/calendar-loco';
import { LocationOnlineMeetingModal } from 'modules/calendar/events/components/location-online-meeting-modal';
import { EventTypeEnum } from 'modules/calendar/events/enums/event-type-enum';
import { hasMeetingLink } from 'modules/calendar/events/utils/online-meeting-utils';
import { useCalendars } from 'modules/profile/integrations/google/hooks/use-calendars';
import React, { PropsWithChildren, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { getUserProfileInfo } from 'shared/api/user-api';
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { isValidUrl } from 'shared/utils/is-valid-url';
import { twMerge } from 'tailwind-merge';
import { EventLocationTypeEnum, linkTypes, locationFields } from '../constants';
import { useCheckZoomIntegration } from '../hooks/use-check-zoom-integration';
import { EventCreateFormInterface, EventEditFormInterface, EventLocationInterface } from '../types/event-interface';
import { LocationIcon, PhoneIcon, ZoomIcon } from '@systemeio/ui/icons';
interface LocationTypeFieldsProps {
  locations: EventLocationInterface[];
  setValue: UseFormSetValue<EventCreateFormInterface | EventEditFormInterface>;
  error?: string;
  eventType: EventTypeEnum;
}
const locationTypeButtons = Object.values(EventLocationTypeEnum).filter(type => !hasMeetingLink(type));
type TempLocationState = EventCreateFormInterface['locations'][number];
const validationRules = {
  [EventLocationTypeEnum.ExternalLink]: 'meetingLink',
  [EventLocationTypeEnum.Personal]: 'address',
  [EventLocationTypeEnum.Phone]: 'phone'
};
export function LocationTypeFields(t0) {
  const $ = _c(93);
  const {
    locations,
    setValue,
    error,
    eventType
  } = t0;
  const {
    t
  } = useLocoTranslation();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      phone: "",
      meetingLink: "",
      address: ""
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [errors, setErrors] = useState(t1);
  const {
    isZoomIntegrated,
    isLoading: isZoomIntegratedLoading
  } = useCheckZoomIntegration();
  const {
    hasGoogleCalendars,
    isLoading: isGoogleCalendarsLoading
  } = useCalendars();
  const isLoading = isGoogleCalendarsLoading || isZoomIntegratedLoading;
  const [tempState, setTempState] = useState(null);
  const [opened, setOpened] = useState(false);
  const t2 = tempState?.locationType;
  let t3;
  if ($[1] !== t2) {
    t3 = hasMeetingLink(t2);
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const isMeetingLinkSelected = t3;
  let t4;
  if ($[3] !== isZoomIntegrated || $[4] !== locations || $[5] !== tempState) {
    t4 = locationType => {
      const isLinkLocationType = hasMeetingLink(locationType);
      const isPhoneLocationType = hasPhoneField(locationType);
      setOpened(true);
      if (isPhoneLocationType) {
        getUserProfileInfo().then(res => {
          tempState && setTempState(prev => ({
            ...prev,
            phone: prev?.phone || res.data.phone
          }) as TempLocationState);
        });
      }
      setTempState(prev_0 => {
        if (isLinkLocationType) {
          const savedLocationType = locations.find(_temp);
          if (savedLocationType) {
            return {
              ...prev_0,
              ...savedLocationType
            };
          } else {
            const defaultLocationType = isZoomIntegrated ? EventLocationTypeEnum.Zoom : locationType;
            return {
              ...prev_0,
              ...locationFields[defaultLocationType],
              locationType: defaultLocationType
            };
          }
        }
        const savedLocationType_0 = locations.find(location_0 => location_0.locationType === locationType);
        return {
          ...prev_0,
          ...locationFields[locationType],
          locationType,
          ...savedLocationType_0
        };
      });
    };
    $[3] = isZoomIntegrated;
    $[4] = locations;
    $[5] = tempState;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const openLocationModal = t4;
  let t5;
  if ($[7] !== tempState) {
    t5 = locations_0 => {
      if (!tempState) {
        return locations_0;
      }
      let newLocations = locations_0;
      if (isOnlineMeetingLocation(tempState.locationType)) {
        newLocations = locations_0.filter(_temp2);
      } else {
        if (isPersonalLocation(tempState.locationType)) {
          newLocations = locations_0.filter(_temp3);
        } else {
          if (isPhoneLocation(tempState.locationType)) {
            newLocations = locations_0.filter(_temp4);
          }
        }
      }
      return newLocations;
    };
    $[7] = tempState;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const filterLocationBasedOnTempState = t5;
  let t6;
  if ($[9] !== filterLocationBasedOnTempState || $[10] !== locations || $[11] !== setValue) {
    t6 = () => {
      setValue("locations", filterLocationBasedOnTempState(locations));
      setOpened(false);
    };
    $[9] = filterLocationBasedOnTempState;
    $[10] = locations;
    $[11] = setValue;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const deleteLocation = t6;
  let t7;
  if ($[13] !== eventType || $[14] !== filterLocationBasedOnTempState || $[15] !== locations || $[16] !== setValue || $[17] !== t || $[18] !== tempState) {
    t7 = () => {
      if (tempState) {
        const missingField = validationRules[tempState.locationType as keyof typeof validationRules];
        if (missingField && !tempState[missingField as keyof typeof tempState]) {
          setErrors(prev_1 => ({
            ...prev_1,
            [missingField]: t("dashboard.validation.empty_error.title")
          }));
          return;
        }
        if (tempState.locationType === EventLocationTypeEnum.ExternalLink) {
          const meetingLink = tempState.meetingLink;
          if (!meetingLink || !isValidUrl(meetingLink)) {
            setErrors(prev_2 => ({
              ...prev_2,
              meetingLink: t("dashboard.calendar.meeting_link_validation.title")
            }));
            return;
          }
        }
        if (eventType === EventTypeEnum.Group) {
          setValue("locations", [tempState]);
        } else {
          setValue("locations", [...filterLocationBasedOnTempState(locations), tempState]);
        }
      }
      setOpened(false);
    };
    $[13] = eventType;
    $[14] = filterLocationBasedOnTempState;
    $[15] = locations;
    $[16] = setValue;
    $[17] = t;
    $[18] = tempState;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const onSubmit = t7;
  let t8;
  if ($[20] !== t) {
    t8 = t("dashboard.calendar.location");
    $[20] = t;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t8) {
    t9 = <FieldLabel required={true} label={t8} />;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== error || $[25] !== eventType || $[26] !== isLoading || $[27] !== locations || $[28] !== openLocationModal || $[29] !== setValue || $[30] !== t) {
    t10 = eventType === EventTypeEnum.Group ? <LocationButton locationType={EventLocationTypeEnum.ExternalLink} openLocationModal={openLocationModal} isFetching={isLoading} isSelected={locations.some(_temp5)}><span className="flex flex-col items-center justify-center gap-3 "><LocationTypeIcon locationType={EventLocationTypeEnum.ExternalLink} />{t(eventLocationLocoKeys[EventLocationTypeEnum.ExternalLink])}</span></LocationButton> : <LocationOnlineMeetingModal locations={locations} error={error} setValue={setValue} />;
    $[24] = error;
    $[25] = eventType;
    $[26] = isLoading;
    $[27] = locations;
    $[28] = openLocationModal;
    $[29] = setValue;
    $[30] = t;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== isLoading || $[33] !== locations || $[34] !== openLocationModal || $[35] !== t) {
    t11 = locationTypeButtons.map(locationType_1 => <LocationButton key={locationType_1} locationType={locationType_1} openLocationModal={openLocationModal} isFetching={isLoading} isSelected={locations.some(x => x.locationType === locationType_1)}><span className="flex flex-col items-center justify-center gap-3"><LocationTypeIcon locationType={locationType_1} />{t(eventLocationLocoKeys[locationType_1])}</span></LocationButton>);
    $[32] = isLoading;
    $[33] = locations;
    $[34] = openLocationModal;
    $[35] = t;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== t10 || $[38] !== t11) {
    t12 = <div className="flex flex-wrap gap-4">{t10}{t11}</div>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== error) {
    t13 = <FieldErrorAndDescription className="mt-2" error={error} />;
    $[40] = error;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setOpened(false);
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== t || $[44] !== tempState) {
    t15 = tempState && t(eventLocationLocoKeys[tempState.locationType]);
    $[43] = t;
    $[44] = tempState;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setTempState(null);
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== errors || $[48] !== hasGoogleCalendars || $[49] !== isMeetingLinkSelected || $[50] !== isZoomIntegrated || $[51] !== t || $[52] !== tempState?.locationType || $[53] !== tempState?.meetingLink) {
    t17 = isMeetingLinkSelected && <div className="flex flex-col gap-5"><RadioGroup optionsWrapperClassName="items-start flex flex-col" options={linkTypes.map(t18 => {
        const {
          id,
          caption
        } = t18;
        if (id === EventLocationTypeEnum.GoogleMeet) {
          return {
            id,
            caption: t(caption),
            tooltipLabel: t("dashboard.calendar.event_inactive_google_meet_location_error"),
            showTooltip: !hasGoogleCalendars,
            disabled: !hasGoogleCalendars
          };
        }
        if (id === EventLocationTypeEnum.Zoom) {
          return {
            id,
            caption: t(caption),
            tooltipLabel: t("dashboard.calendar.event_inactive_zoom_location_error"),
            showTooltip: !isZoomIntegrated,
            disabled: !isZoomIntegrated
          };
        }
        return {
          id,
          caption: t(caption)
        };
      }).filter(Boolean)} selected={tempState?.locationType} onChange={val => {
        setTempState(prev_3 => prev_3 && {
          ...prev_3,
          locationType: val as EventLocationTypeEnum
        });
      }} />{tempState?.locationType === EventLocationTypeEnum.ExternalLink && <FormInput value={tempState?.meetingLink || ""} onChange={e => {
        if (errors.meetingLink) {
          setErrors(_temp6);
        }
        setTempState(prev_5 => prev_5 && {
          ...prev_5,
          meetingLink: e.target.value
        });
      }} label={t("dashboard.calendar.event_location_meeting_link")} required={true} error={errors.meetingLink} />}</div>;
    $[47] = errors;
    $[48] = hasGoogleCalendars;
    $[49] = isMeetingLinkSelected;
    $[50] = isZoomIntegrated;
    $[51] = t;
    $[52] = tempState?.locationType;
    $[53] = tempState?.meetingLink;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] !== errors || $[56] !== t || $[57] !== tempState?.locationType || $[58] !== tempState?.phone) {
    t18 = tempState?.locationType === EventLocationTypeEnum.Phone && <FormInputPhone label={t("dashboard.calendar.event_location.phone")} value={tempState?.phone} onChange={value => {
      if (errors.phone) {
        setErrors(_temp7);
      }
      setTempState(prev_7 => prev_7 && {
        ...prev_7,
        phone: value
      });
    }} maxLength={20} required={true} error={errors.phone} />;
    $[55] = errors;
    $[56] = t;
    $[57] = tempState?.locationType;
    $[58] = tempState?.phone;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  let t19;
  if ($[60] !== errors || $[61] !== t || $[62] !== tempState) {
    t19 = tempState?.locationType === EventLocationTypeEnum.Personal && <div className="flex flex-col gap-5"><FormInput name="location.address" value={tempState.address || ""} onChange={e_0 => {
        if (errors.address) {
          setErrors(_temp8);
        }
        setTempState(prev_9 => prev_9 && {
          ...prev_9,
          address: e_0.target.value
        });
      }} label={t("dashboard.calendar.event_location_address")} required={true} error={errors.address} /><FormInputPhone label={t("dashboard.calendar.event_location.phone")} value={tempState.phone} onChange={value_0 => {
        setTempState(prev_10 => prev_10 && {
          ...prev_10,
          phone: value_0
        });
      }} maxLength={20} /></div>;
    $[60] = errors;
    $[61] = t;
    $[62] = tempState;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  let t20;
  if ($[64] !== deleteLocation) {
    t20 = e_1 => {
      e_1.preventDefault();
      deleteLocation();
    };
    $[64] = deleteLocation;
    $[65] = t20;
  } else {
    t20 = $[65];
  }
  let t21;
  if ($[66] !== t) {
    t21 = t("global.delete");
    $[66] = t;
    $[67] = t21;
  } else {
    t21 = $[67];
  }
  let t22;
  if ($[68] !== t20 || $[69] !== t21) {
    t22 = <DangerButton className="w-full" width="large" onClick={t20}>{t21}</DangerButton>;
    $[68] = t20;
    $[69] = t21;
    $[70] = t22;
  } else {
    t22 = $[70];
  }
  let t23;
  if ($[71] !== onSubmit) {
    t23 = e_2 => {
      e_2.preventDefault();
      onSubmit();
    };
    $[71] = onSubmit;
    $[72] = t23;
  } else {
    t23 = $[72];
  }
  let t24;
  if ($[73] !== t) {
    t24 = t("global.save");
    $[73] = t;
    $[74] = t24;
  } else {
    t24 = $[74];
  }
  let t25;
  if ($[75] !== t23 || $[76] !== t24) {
    t25 = <PrimaryButton className="w-full" width="large" onClick={t23}>{t24}</PrimaryButton>;
    $[75] = t23;
    $[76] = t24;
    $[77] = t25;
  } else {
    t25 = $[77];
  }
  let t26;
  if ($[78] !== t22 || $[79] !== t25) {
    t26 = <div className="mt-6 flex gap-4 lg:mt-10">{t22}{t25}</div>;
    $[78] = t22;
    $[79] = t25;
    $[80] = t26;
  } else {
    t26 = $[80];
  }
  let t27;
  if ($[81] !== opened || $[82] !== t15 || $[83] !== t17 || $[84] !== t18 || $[85] !== t19 || $[86] !== t26) {
    t27 = <Modal opened={opened} onClose={t14} title={t15} size={ModalSizeEnum.small} afterLeave={t16}>{t17}{t18}{t19}{t26}</Modal>;
    $[81] = opened;
    $[82] = t15;
    $[83] = t17;
    $[84] = t18;
    $[85] = t19;
    $[86] = t26;
    $[87] = t27;
  } else {
    t27 = $[87];
  }
  let t28;
  if ($[88] !== t12 || $[89] !== t13 || $[90] !== t27 || $[91] !== t9) {
    t28 = <div className="order-2 sm:order-1">{t9}{t12}{t13}{t27}</div>;
    $[88] = t12;
    $[89] = t13;
    $[90] = t27;
    $[91] = t9;
    $[92] = t28;
  } else {
    t28 = $[92];
  }
  return t28;
}
function _temp8(prev_8) {
  return {
    ...prev_8,
    address: ""
  };
}
function _temp7(prev_6) {
  return {
    ...prev_6,
    phone: ""
  };
}
function _temp6(prev_4) {
  return {
    ...prev_4,
    meetingLink: ""
  };
}
function _temp5(t0) {
  const {
    locationType: locationType_0
  } = t0;
  return hasMeetingLink(locationType_0);
}
function _temp4(location_3) {
  return !isPhoneLocation(location_3.locationType);
}
function _temp3(location_2) {
  return !isPersonalLocation(location_2.locationType);
}
function _temp2(location_1) {
  return !isOnlineMeetingLocation(location_1.locationType);
}
function _temp(location) {
  return hasMeetingLink(location.locationType);
}
interface LocationButtonProps extends PropsWithChildren {
  locationType: EventLocationTypeEnum;
  openLocationModal: (locationType: EventLocationTypeEnum) => void;
  isSelected: boolean;
  isFetching?: boolean;
}
export const LocationButton = t0 => {
  const $ = _c(11);
  const {
    locationType,
    openLocationModal,
    isSelected,
    isFetching,
    children
  } = t0;
  let t1;
  if ($[0] !== isSelected) {
    t1 = twMerge("h-28 w-32 rounded-md border md:h-32 md:w-36", twMerge(isSelected && "border-brand-primary [&>div>span>svg]:fill-brand-primary outline-2"));
    $[0] = isSelected;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== locationType || $[3] !== openLocationModal) {
    t2 = () => {
      openLocationModal(locationType);
    };
    $[2] = locationType;
    $[3] = openLocationModal;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== children || $[6] !== isFetching || $[7] !== locationType || $[8] !== t1 || $[9] !== t2) {
    t3 = <NeutralButton type="button" key={locationType} isFetching={isFetching} className={t1} onClick={t2}>{children}</NeutralButton>;
    $[5] = children;
    $[6] = isFetching;
    $[7] = locationType;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
export function LocationTypeIcon(t0) {
  const $ = _c(3);
  const {
    locationType
  } = t0;
  switch (locationType) {
    case EventLocationTypeEnum.ExternalLink:
      {
        let t1;
        if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <ZoomIcon />;
          $[0] = t1;
        } else {
          t1 = $[0];
        }
        return t1;
      }
    case EventLocationTypeEnum.Phone:
      {
        let t1;
        if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <PhoneIcon />;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        return t1;
      }
    case EventLocationTypeEnum.Personal:
      {
        let t1;
        if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <LocationIcon />;
          $[2] = t1;
        } else {
          t1 = $[2];
        }
        return t1;
      }
    default:
      {
        return null;
      }
  }
}
function hasPhoneField(locationType?: EventLocationTypeEnum) {
  return !!locationType && [EventLocationTypeEnum.Personal, EventLocationTypeEnum.Phone].includes(locationType);
}
function isOnlineMeetingLocation(locationType?: EventLocationTypeEnum) {
  return !!locationType && [EventLocationTypeEnum.ExternalLink, EventLocationTypeEnum.Zoom, EventLocationTypeEnum.GoogleMeet].includes(locationType);
}
function isPhoneLocation(locationType?: EventLocationTypeEnum) {
  return EventLocationTypeEnum.Phone === locationType;
}
function isPersonalLocation(locationType?: EventLocationTypeEnum) {
  return EventLocationTypeEnum.Personal === locationType;
}